import React from 'react';
import { Box, Card, CardContent, Typography, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';

interface FeedbackReportCardProps {
  title: string;
  icon: React.ReactNode;
  color: string;
  status: string;
  statusType: 'success' | 'warning' | 'error';
  step: number;
  children: React.ReactNode;
}

// Helper for the stepper/progress bar
const FeedbackStepper = ({ color, activeStep }: { color: string; activeStep: number }) => (
  <Box display="flex" alignItems="center" mb={2}>
    {[1, 2, 3, 4].map((step) => (
      <Box
        key={step}
        sx={{
          width: 32,
          height: 8,
          borderRadius: 4,
          backgroundColor: step === activeStep ? color : '#a0a0a0eb',
          opacity: step <= activeStep ? 1 : 0.2,
          mx: step > 0 ? 0.5 : 0,
          transition: 'background 0.2s',
        }}
      />
    ))}
    {color === '#4CAF50' && <CheckCircleIcon sx={{ color, ml: 1 }} fontSize="small" />}
    {color === '#FFC107' && <InfoIcon sx={{ color, ml: 1 }} fontSize="small" />}
    {color === '#F44336' && <ErrorIcon sx={{ color, ml: 1 }} fontSize="small" />}
  </Box>
);

const FeedbackReportCard: React.FC<FeedbackReportCardProps> = ({
  title,
  icon,
  color,
  status,
  statusType,
  step,
  children,
}) => {
  return (
    <Card
      sx={{
        border: `2px solid ${color}`,
        borderRadius: 3,
        minWidth: 300,
        boxShadow: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        }
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" mb={1}>
          <Box sx={{
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(90deg, rgba(255,0,192,1) 0%, rgba(255,109,21,1) 90%)',
            borderRadius: '50%',
            mr: 2,
            '& svg': { color: 'white', fontSize: 24 }
          }}>
            {icon}
          </Box>
          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>
        </Box>
        <FeedbackStepper color={color} activeStep={step} />
        <Chip
          label={status}
          color={statusType}
          sx={{ mb: 2, 
            fontWeight: 'bold', 
            fontSize: 16, 
            backgroundColor: color,
            px: 2}}
          icon={statusType === 'success' ? <CheckCircleIcon sx={{ color }} /> : statusType === 'warning' ? <InfoIcon sx={{ color }} /> : <ErrorIcon sx={{ color }} />}
        />
        {children}
      </CardContent>
    </Card>
  );
};

export default FeedbackReportCard;